import { audio } from "../audio/bus.js";
import { intercom } from "./store.svelte.js";

/**
 * Receive-side radio chain, one per crewmate. Voice is transmitted clean; we ruin it here:
 *   source → high-pass 300 Hz → low-pass 3400 Hz → light waveshaping → voice gain ─┐
 *   white noise → noise gain (rises as quality falls) ───────────────────────────┴→ intercom bus
 * A VAD on the analyser fires the squelch tail when a transmission ends. Intelligible at all but
 * crisis quality — atmospherically ruined, never actually ruined.
 */
interface Chain {
  el: HTMLAudioElement; src: MediaStreamAudioSourceNode; hp: BiquadFilterNode; lp: BiquadFilterNode;
  shaper: WaveShaperNode; voice: GainNode; noise: AudioBufferSourceNode; noiseGain: GainNode;
  analyser: AnalyserNode; speaking: boolean; quietSince: number; timer: number;
}

function curve(amount: number): Float32Array<ArrayBuffer> {
  const n = 256, c = new Float32Array(new ArrayBuffer(n * 4));
  const k = amount * 40;
  for (let i = 0; i < n; i++) { const x = (i * 2) / n - 1; c[i] = ((1 + k) * x) / (1 + k * Math.abs(x)); }
  return c;
}

class Radio {
  private chains = new Map<string, Chain>();
  private noiseBuffer: AudioBuffer | null = null;
  /** Per-crew channel level, 0..1, set from the menu and persisted per browser. */
  private levels: Record<string, number> = (() => { try { return JSON.parse(localStorage.getItem("spacecrew.crewAudio") ?? "{}"); } catch { return {}; } })();
  private lastQuality: Record<string, number> = {};

  level(id: string): number { return this.levels[id] ?? 1; }
  setLevel(id: string, value: number): void {
    this.levels[id] = Math.max(0, Math.min(1, value));
    try { localStorage.setItem("spacecrew.crewAudio", JSON.stringify(this.levels)); } catch { /* private mode */ }
    this.setQuality(id, this.lastQuality[id] ?? 1);
  }

  attach(id: string, stream: MediaStream): void {
    const ctx = audio.context, bus = audio.intercomBus;
    if (!ctx || !bus) { setTimeout(() => this.attach(id, stream), 500); return; } // audio not unlocked yet
    this.detach(id);
    // Chrome only delivers remote audio to Web Audio once a media element "plays" the stream.
    const el = new Audio(); el.srcObject = stream; el.muted = true; void el.play().catch(() => {});
    const src = ctx.createMediaStreamSource(stream);
    const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 300; hp.Q.value = 0.7;
    const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 3400; lp.Q.value = 0.7;
    const shaper = ctx.createWaveShaper(); shaper.curve = curve(0.15); shaper.oversample = "2x";
    const voice = ctx.createGain(); voice.gain.value = 1;
    const analyser = ctx.createAnalyser(); analyser.fftSize = 512;
    if (!this.noiseBuffer) {
      const b = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate); const d = b.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      this.noiseBuffer = b;
    }
    const noise = ctx.createBufferSource(); noise.buffer = this.noiseBuffer; noise.loop = true;
    const noiseGain = ctx.createGain(); noiseGain.gain.value = 0.02;
    const noiseLp = ctx.createBiquadFilter(); noiseLp.type = "bandpass"; noiseLp.frequency.value = 1800; noiseLp.Q.value = 0.5;
    src.connect(hp).connect(lp).connect(shaper).connect(voice).connect(bus);
    src.connect(analyser);
    noise.connect(noiseLp).connect(noiseGain).connect(bus);
    noise.start();
    const chain: Chain = { el, src, hp, lp, shaper, voice, noise, noiseGain, analyser, speaking: false, quietSince: 0, timer: 0 };
    const buf = new Uint8Array(analyser.fftSize);
    chain.timer = window.setInterval(() => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0; for (let i = 0; i < buf.length; i++) { const v = (buf[i]! - 128) / 128; sum += v * v; }
      const rms = Math.sqrt(sum / buf.length);
      const now = performance.now();
      if (rms > 0.04) { if (!chain.speaking) { chain.speaking = true; intercom.setPeer(id, { speaking: true }); } chain.quietSince = now; }
      // No sound on release: an open mic's noise floor flips this every few seconds, and a chirp
      // that often reads as a fault rather than as radio. The speaking indicator is enough.
      else if (chain.speaking && now - chain.quietSince > 350) { chain.speaking = false; intercom.setPeer(id, { speaking: false }); }
    }, 60);
    this.chains.set(id, chain);
  }

  /** Map received quality 0..1 onto the chain. Voice stays intelligible down to ~0.2. */
  setQuality(id: string, q: number): void {
    const c = this.chains.get(id), ctx = audio.context;
    if (!c || !ctx) return;
    this.lastQuality[id] = q;
    const vol = this.level(id);
    const t = ctx.currentTime, bad = 1 - q;
    c.hp.frequency.setTargetAtTime(300 + bad * 500, t, 0.2);        // 300 → 800 Hz
    c.lp.frequency.setTargetAtTime(3400 - bad * 1800, t, 0.2);      // 3400 → 1600 Hz
    c.shaper.curve = curve(0.15 + bad * 0.6);
    c.voice.gain.setTargetAtTime(q < 0.12 ? 0 : (0.75 + q * 0.25) * vol, t, 0.2); // signal lost below 0.12
    c.noiseGain.gain.setTargetAtTime((0.015 + bad * bad * 0.25) * Math.max(vol, 0.15), t, 0.2);
  }

  detach(id: string): void {
    const c = this.chains.get(id);
    if (!c) return;
    window.clearInterval(c.timer);
    try { c.noise.stop(); } catch { /* already stopped */ }
    for (const n of [c.src, c.hp, c.lp, c.shaper, c.voice, c.noiseGain, c.analyser]) n.disconnect();
    c.el.srcObject = null;
    this.chains.delete(id);
  }
}

export const radio = new Radio();
